<template>
  <div class="mb-4">
    <validation-observer ref="cbObserver" v-slot="{ errors }">
      <validation-provider ref="recaptchaProvider" 
        :rules="hasResponse ? { cbRequired: !isOptional && verifiedStatus < 2 ? { question, stateResponse } : false } : { required: !isOptional && verifiedStatus < 2 }" 
        :disabled="isOptional || verifiedStatus > 1">
        <div v-for="(error, name) in errors" :key="name">
          <h6 v-if="displayError(error, number + question.questionText)" class="text-danger">{{ error[0] }}</h6>
        </div>
        <div :style="{ 'background-color': questionBackColor }"><span v-html="number" class="number-text"></span><span v-html="question.questionText"></span></div>
        <div class="font-italic mb-1">{{ question.instructions }}</div>
        <span v-if="debugQuestions" class="text-warning">{{ question.hspCategoryQuestionId }} (state: {{ stateResponse }}, text: {{ stateResponseTextValue }}))</span>
        <div v-if="hasResponse" :style="{ 'background-color': responsesBackColor }">
          <div v-for="response in responses" :key="response.hspResponseId">
            <base-checkbox
              :exclusiveSelected="exclusiveSelected"
              :hspResponseId="response.hspResponseId"
              :isExclusive="response.hspResponseTypeId == 1"
              :checked="isChecked"
              :textValue="stateResponseTextValue"
              :disabled="verifiedStatus > 0"
              @input="handleInput"><span v-html="response.responseTitle"></span></base-checkbox>
          </div>
        </div>
        <div v-else id="recaptchaRender"></div>
      </validation-provider>
    </validation-observer>
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { mapGetters, mapActions } from 'vuex';
import { getResponses } from 'src/modules/utils';
import Vue from "vue";
import { VueReCaptcha } from 'vue-recaptcha-v3'
Vue.use(VueReCaptcha, { 
  siteKey: process.env.VUE_APP_RECAPTCHA3_SITE_KEY,
  loaderOptions: {
    useRecaptchaNet: true,
    autoHideBadge: true
  }
});

export default {
  components: {
    ValidationObserver,
    ValidationProvider
  },
  props: {
    question: {
      type: Object,
      required: true,
      default: function() {
        return {
          responses: []
        }
      }
    },
    number: String,
    questionBackColor: String,
    responsesBackColor: String,
    sectionId: {
      type: Number,
      required: true,
      description: 'Needed to disable validation when section is not active.'
    },
  },
  data() {
    return {
      exclusiveSelected: false,
      responses: [],
      verifiedStatus: 0,  // 0 - unverified, 1 - verifying, 2 - verified
      siteKeyV2: process.env.VUE_APP_RECAPTCHA2_SITE_KEY,
      siteKeyV3: process.env.VUE_APP_RECAPTCHA3_SITE_KEY,
      failedVerify: 0,
      hasResponse: false,
      reCaptchaVersion: 3,
      verificationExpired: false,
    }
  },
  computed: {
    questionText() {
      return typeof this.question === "string" ? this.question : this.question.questionText;
    },
    isDisplayed() {
      return this.sectionId === this.activeSectionId;
    },
    isOptional() {
      if (this.isDisplayed && this.question.isRequired === true) {
        return false;
      }

      return true;
    },
    stateResponse() {
      return this.$store.getters.getQuestionResponseIds(this.question.hspCategoryQuestionId);
    },
    siteKey() {
      return this.getVersionSiteKey(this.reCaptchaVersion);
    },
    stateResponseTextValue() {
      let responses = this.$store
        .getters
        .getQuestionResponses(this.question.hspCategoryQuestionId)
        .filter(response => response.hspResponseTypeId == 2);

      if (responses.length) {
        return responses[0].responseTextValue;
      }

      return null;
    },
    isChecked() {
      let hspResponseId = this.responses.length > 0 ? this.responses[0].hspResponseId : -1;
      if (this.stateResponse.indexOf(hspResponseId) >= 0) {
        return true;
      }

      return false;
    },
    responseHasReset() {
      if (!this.hasResponse) {
        return false;
      }

      if (this.verifiedStatus > 1 && !this.isChecked) {
        return true;
      }

      return false;
    },
    ...mapGetters([
      'colorScheme',
      'activeSectionId',
      'debugQuestions'
    ])
  },
  methods: {
    displayError(error, description = "") {
      let display = error.length > 0 && error[0].length > 0;
      // console.log("In ReCapthca.vue -- error:");
      // console.log(error);
      // console.log((description.length > 0 ? "for [" + description + "]: " : "") + "display = [" + display + "]");
      return display;
    },
    getVersionSiteKey(version) {
      if (typeof version === "undefined") {
        return process.env.VUE_APP_RECAPTCHA3_SITE_KEY;
      }

      if (version === 2) {
        return process.env.VUE_APP_RECAPTCHA2_SITE_KEY;
      }

      return process.env.VUE_APP_RECAPTCHA3_SITE_KEY;
    },
    getVersionSecretKey(version) {
      if (typeof version === "undefined") {
        return process.env.VUE_APP_RECAPTCHA3_SECRET_KEY;
      }

      if (version === 2) {
        return process.env.VUE_APP_RECAPTCHA2_SECRET_KEY;
      }

      return process.env.VUE_APP_RECAPTCHA3_SECRET_KEY;
    },
    async recaptcha(response, version) {
      let recaptchaVer = typeof version !== "undefined" && version !==  null ? version : 3;

      let token = response;
      if (!token) {
        // Execute reCAPTCHA with action "login".
        token = await this.$recaptcha('login')
      }

      if (!token) {
        return { sucess: false, errors: "Failed token" };
      }

      let options = {
        "OrgSurveyUserId": null,
        "IsSecure": false,
        "Token": token,
        "Version": recaptchaVer,
      }
      this.verifiedStatus = 1;
      let result = await this.verifyRecaptcha(options);

      return result;
    },
    dispatchCheckbox(value, params) {
      this.$store.dispatch('setCheckboxesQuestionResponse', {
        include: value,
        questionTypeId: this.question.hspQuestionTypeId,
        questionId: this.question.hspCategoryQuestionId,
        responseId: params.hspResponseId,
        isExclusive: params.isExclusive,
        hasTextField: false
      });

      if (params.isExclusive) {
        this.exclusiveSelected = value;
      }
      else {
        this.exclusiveSelected = false;
      }

    },
    handleInput: async function(value, params) {
      this.dispatchCheckbox(value, params);
      if (value) {
        let result = await this.recaptcha(null, this.reCaptchaVersion);
        if (!result.success) {
          this.verifiedStatus = 0;
          this.$recaptchaInstance.recaptcha.reset();
          // Uncheck the checkbox
          this.dispatchCheckbox(false, params);
          return;
        }

        this.verifiedStatus = 2;
      }

      this.$refs.cbObserver.reset();
    },
    async verifyRecaptchaV2(response) {
      let result = await this.recaptcha(response, this.reCaptchaVersion);
      if (!result.success) {
        this.verifiedStatus = 0;
        return;
      }

      if (this.verificationExpired) {
        return;
      }

      this.verifiedStatus = 2;
      this.$refs.recaptchaProvider.reset();
    },
    async setRecaptchaType() {
      if (this.hasResponse) {
        this.reCaptchaVersion = 3;
        if (typeof this.$recaptchaInstance !== "undefined") {
          this.$recaptchaInstance.siteKey = this.siteKey;
        }
        await this.$recaptchaLoaded();
      }
      else {
        this.reCaptchaVersion = 2;
        if (typeof this.$recaptchaInstance !== "undefined") {
          this.$recaptchaInstance.siteKey = this.siteKey;
        }
        await this.$recaptchaLoaded();
        this.$recaptchaInstance.recaptcha.render('recaptchaRender', {
          'sitekey' : this.$recaptchaInstance.siteKey,
          'callback': (response) => {
            this.verificationExpired = false;
            this.verifyRecaptchaV2(response);
          },
          'expired-callback': () => {
            this.verificationExpired = true;
          }
        });
      }
    },
    async initRecaptcha(responses) {
      responses = responses || getResponses(this.question);
      this.hasResponse = typeof responses !== "undefined" && responses.length > 0 ? true : false;
      if (this.hasResponse && this.stateResponse.indexOf(responses[0].hspResponseId) >= 0) {
        this.verifiedStatus = 2;
      }

      await this.setRecaptchaType();
    },
    ...mapActions([
        'verifyRecaptcha',
      ]),
  },
  watch: {
    isDisplayed: function(newValue, oldValue) {
      if (newValue === oldValue) {
        return;
      }

      // Hide badge when navigating away into a page without reCAPTCHA.
      // Show badge when navigating into or back in the reCAPTCHA page.
      if (newValue) {
        this.$recaptchaInstance.showBadge();
      }
      else {
        this.$recaptchaInstance.hideBadge();
      }
    },
    verificationExpired: function(newValue, oldValue) {
      if (newValue !== oldValue && newValue) {
        this.verifiedStatus = 0;
      }
    },
    responseHasReset: function(newValue, oldValue) {
      if (newValue !== oldValue && newValue) {
        this.verifiedStatus = 0;
      }
    },
  },
  async created() {
    this.responses = getResponses(this.question);

    await this.initRecaptcha(this.responses);
  },
  mounted() {
    this.$refs.recaptchaProvider.reset();
  }
}
</script>
